import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { waLink, DEFAULT_WA_MESSAGE } from "../lib/whatsapp";

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#0a0a0a]">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.6, ease: [0.16, 1, 0.3, 1] }}
          src="/images/hero.png"
          alt="Salle de réception illuminée avec la brigade FK en formation"
          data-testid="hero-image"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/85 via-[#0a0a0a]/45 to-[#0a0a0a]/15" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0a0a0a]/60 to-transparent" />
      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pb-28 pt-36 lg:px-10">
        <div className="max-w-3xl">
          <RevealLine delay={0.35}>
            <span className="mb-8 inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-gold" data-testid="hero-eyebrow">
              <span className="h-px w-10 bg-gold" />
              FK Conseil &amp; Événement
            </span>
          </RevealLine>
          <h1 className="font-display text-[13vw] leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-[5.6rem]" data-testid="hero-title">
            <RevealLine delay={0.5}>L'art du</RevealLine>
            <RevealLine delay={0.62}>
              <em className="font-light italic text-gold">service</em>
            </RevealLine>
            <RevealLine delay={0.74}>d'exception.</RevealLine>
          </h1>
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 max-w-md text-base leading-relaxed text-white/70 sm:text-lg"
          >
            Serveurs, serveuses, hôtes &amp; hôtesses d'accueil et maîtres d'hôtel pour vos
            mariages, vins d'honneur, conférences et réceptions privées.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <a
              href="#devis"
              data-testid="hero-devis-cta"
              className="group relative overflow-hidden rounded-full bg-gold px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03]"
            >
              Demander un devis
            </a>
            <a
              href={waLink(DEFAULT_WA_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="rounded-full border border-white/30 px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/85 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              WhatsApp direct
            </a>
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.7, ease: [0.16, 1, 0.3, 1] }}
        className="absolute bottom-8 right-6 hidden rounded-2xl border border-white/15 bg-[#0a0a0a]/60 px-6 py-4 backdrop-blur-xl lg:block"
        data-testid="hero-badge"
      >
        <p className="font-display text-3xl text-gold">100%</p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/60">Équipe en tenue d'apparat</p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-gold/70" />
      </motion.div>
    </section>
  );
};
